import { useState } from 'react';
import {
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Alert,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { FilterBar } from '../../components/FilterBar';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useSubscriptions, useCancelSubscription } from './useSubscriptions';

/**
 * Subscriptions · Liste + Cancel.
 * Spec: orbit-specs/done/subscriptions-admin.md
 *
 * Shape per Spec (flach, nicht nested):
 *  - userId, userEmail
 *  - unitAmount, currency, billingInterval
 *  - status (7 Werte inkl. incomplete/paused/ended)
 *  - providerSubscriptionId (null = noch incomplete Checkout)
 *  - trialEndAt + trialEndingEmittedAt (Cron-Idempotency-Marker)
 */

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const STATUSES = ['', 'incomplete', 'trialing', 'active', 'past_due', 'cancelled', 'paused', 'ended'];
const PROVIDERS = ['', 'stripe', 'paypal'];
const CANCELLABLE = new Set(['active', 'trialing', 'past_due']);

export function SubscriptionsPage() {
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [userId, setUserId] = useState('');
  const [pendingCancel, setPendingCancel] = useState(null);

  const query = useSubscriptions({ status, provider, userId });
  const rows = query.data?.subscriptions ?? [];
  const hasFilters = status || provider || userId;
  // Date.now() ist impure → wir nehmen den Stamp vom letzten Query-Fetch.
  // Im ersten Render (vor Daten) Fallback auf 0; Row-Comparisons werden dann
  // im Loading-State eh ausgeblendet weil rows leer ist.
  const nowMs = query.dataUpdatedAt;
  const threeDaysOut = nowMs ? nowMs + 3 * 86_400_000 : 0;

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        subtitle="Brand-scoped · GET /v1/admin/subscriptions"
      />
      <FilterBar
        onReset={hasFilters ? () => { setStatus(''); setProvider(''); setUserId(''); } : undefined}
        extras={
          <>
            <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 160 }}>
              {STATUSES.map((s) => (
                <option key={s || 'all'} value={s}>{s || 'Alle Status'}</option>
              ))}
            </Input>
            <Input type="select" bsSize="sm" value={provider} onChange={(e) => setProvider(e.target.value)} style={{ maxWidth: 140 }}>
              {PROVIDERS.map((p) => (
                <option key={p || 'all'} value={p}>{p || 'Alle Provider'}</option>
              ))}
            </Input>
            <Input
              bsSize="sm"
              placeholder="User-ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 110 }}
            />
          </>
        }
      />
      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!rows.length}
        emptyMessage="Keine Subscriptions."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 80 }}>ID</th>
            <th>Customer</th>
            <th>Plan</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 90 }}>Provider</th>
            <th style={{ width: 140 }}>Period-Ende</th>
            <th style={{ width: 130 }}>Trial</th>
            <th style={{ width: 130 }}>Mindestlfz.</th>
            <th style={{ width: 110 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const termMs = s.minimumTermUntil ? Date.parse(s.minimumTermUntil) : 0;
            const trialMs = s.trialEndAt ? Date.parse(s.trialEndAt) : 0;
            const inTerm = termMs > nowMs;
            const trialEnding = trialMs > nowMs && trialMs < threeDaysOut && !s.trialEndingEmittedAt;
            const dimmed = ['cancelled', 'ended'].includes(s.status);
            return (
              <tr key={s.id} className={dimmed ? 'opacity-50' : undefined}>
                <td>#{s.id}</td>
                <td className="small">
                  {s.userEmail}
                  <div className="text-muted">#{s.userId}</div>
                </td>
                <td>
                  <div className="fw-semibold small">{s.planLabel ?? s.sku}</div>
                  <div className="small text-muted">
                    {fmtEur.format(s.unitAmount / 100)}/{s.billingInterval}
                  </div>
                </td>
                <td>
                  <StatusBadge value={s.status} />
                  {s.cancelAtPeriodEnd && (
                    <Badge color="warning" pill className="ms-1" style={{ fontSize: 10 }}>
                      kündigt zum Period-End
                    </Badge>
                  )}
                </td>
                <td>
                  <code style={{ fontSize: 12 }}>{s.provider}</code>
                  {s.providerSubscriptionId === null && (
                    <div className="small text-muted">incomplete checkout</div>
                  )}
                </td>
                <td>
                  <RelativeTime iso={s.currentPeriodEnd} />
                </td>
                <td>
                  {s.trialEndAt ? (
                    <div>
                      <RelativeTime iso={s.trialEndAt} />
                      {trialEnding && (
                        <Badge color="warning" pill className="d-block mt-1" style={{ fontSize: 10 }}>
                          sweep fällig
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
                <td>
                  {inTerm ? (
                    <div>
                      <Badge color="warning" pill>läuft</Badge>
                      <div className="small text-muted">bis <RelativeTime iso={s.minimumTermUntil} /></div>
                    </div>
                  ) : (
                    <span className="text-muted small">erfüllt</span>
                  )}
                </td>
                <td className="text-end">
                  {CANCELLABLE.has(s.status) && (
                    <HasPermission perms={['subscriptions.write']}>
                      <Button color="danger" outline size="sm" onClick={() => setPendingCancel(s)}>
                        Kündigen
                      </Button>
                    </HasPermission>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>

      {pendingCancel && (
        <CancelSubscriptionModal
          subscription={pendingCancel}
          onClose={() => setPendingCancel(null)}
        />
      )}
    </div>
  );
}

function CancelSubscriptionModal({ subscription, onClose }) {
  const [immediate, setImmediate] = useState(false);
  const [reason, setReason] = useState('');
  const cancel = useCancelSubscription();

  const inTerm = subscription.minimumTermUntil
    && new Date(subscription.minimumTermUntil) > new Date();

  const onSubmit = async (e) => {
    e.preventDefault();
    await cancel.mutateAsync({
      id: subscription.id,
      immediate,
      reason: reason.trim() || undefined,
    });
    onClose();
  };

  return (
    <Modal isOpen toggle={cancel.isPending ? undefined : onClose}>
      <ModalHeader toggle={cancel.isPending ? undefined : onClose}>
        Subscription #{subscription.id} kündigen
      </ModalHeader>
      <form onSubmit={onSubmit}>
        <ModalBody>
          {immediate && (
            <Alert color="danger" className="small">
              <strong>Sofort-Beendigung:</strong> Kunde verliert Zugang sofort.
              {inTerm && (
                <span> Mindestlaufzeit endet erst <RelativeTime iso={subscription.minimumTermUntil} />
                  {' '}— ggf. Pro-Rata-Refund manuell prüfen (`/v1/admin/invoices/:id/credit-note`).
                </span>
              )}
            </Alert>
          )}
          {!immediate && (
            <Alert color="info" className="small">
              Subscription wird zum nächsten Period-Ende beendet
              ({subscription.currentPeriodEnd ? <RelativeTime iso={subscription.currentPeriodEnd} /> : '—'}).
              Bis dahin bleibt der Zugang erhalten.
            </Alert>
          )}
          <FormGroup>
            <Label className="small fw-semibold">Modus</Label>
            <div>
              <FormGroup check className="mb-1">
                <Input type="radio" name="cancelMode" id="cancelPeriod" checked={!immediate} onChange={() => setImmediate(false)} />
                <Label check for="cancelPeriod" className="small">Zum Period-Ende</Label>
              </FormGroup>
              <FormGroup check className="mb-0">
                <Input type="radio" name="cancelMode" id="cancelImmediate" checked={immediate} onChange={() => setImmediate(true)} />
                <Label check for="cancelImmediate" className="small">Sofort beenden</Label>
              </FormGroup>
            </div>
          </FormGroup>
          <FormGroup>
            <Label className="small">Grund <span className="text-muted">(optional, für Audit)</span></Label>
            <Input
              type="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Customer-Wunsch / past_due_uncollectable / fraud"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={cancel.isPending}>
            Abbrechen
          </Button>
          <Button color="danger" type="submit" disabled={cancel.isPending}>
            {cancel.isPending ? 'Kündigen…' : (immediate ? 'Sofort kündigen' : 'Zum Period-Ende kündigen')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
